
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Book } from "@/types/book";
import { useBundles } from "@/hooks/use-bundles";
import { useIndividualBookAccess } from "@/hooks/use-individual-book-access";
import { useAuth } from "@/components/AuthProvider";
import { useAppConfig } from "@/hooks/use-app-config";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";

export interface SmartAvailability {
  // Access status
  hasAccess: boolean;
  hasIndividualAccess: boolean;
  hasBundleAccess: boolean;
  isAdmin: boolean;
  
  // Bundle information
  bundleStatus: 'active' | 'upcoming' | 'expired' | 'permanent' | null;
  bundleTitle: string | null;
  bundlePrice: number | null;
  earlyAccessPrice: number | null;
  
  // Individual purchase
  canPurchaseIndividually: boolean;
  individualPrice: number | null;
  
  // Cooldown information
  isInCooldown: boolean;
  cooldownEndDate: string | null;
  daysUntilAvailable: number | null;
  
  // Credits
  userCredits: number;
  canAffordWithCredits: boolean;
  creditsRequired: number;
  
  // Primary action
  primaryAction: 'read' | 'bundle' | 'individual' | 'credits' | 'cooldown' | 'early_access' | 'not_available';
  primaryActionText: string;
  primaryActionPrice: number | null;
  
  // Smart messaging
  availabilityMessage: string;
  explanationMessage: string;
  
  // Visual indicators
  statusBadge: {
    text: string;
    color: 'green' | 'blue' | 'orange' | 'purple' | 'gray';
  };
}

export function useSmartBookAvailability(book: Book | null) {
  const { session } = useAuth();
  const { data: appConfig } = useAppConfig();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const {
    activeBundle,
    expiredBundles,
    permanentBundles,
    hasActiveAccess,
    permanentBundleAccess,
    expiredBundleAccess
  } = useBundles();
  
  const { data: hasIndividualAccess } = useIndividualBookAccess(book?.id || null);
  
  return useQuery({
    queryKey: ['smartBookAvailability', book?.id, session?.user?.id],
    queryFn: async (): Promise<SmartAvailability> => {
      if (!book) {
        return {
          hasAccess: false,
          hasIndividualAccess: false,
          hasBundleAccess: false,
          isAdmin: false,
          bundleStatus: null,
          bundleTitle: null,
          bundlePrice: null,
          earlyAccessPrice: null,
          canPurchaseIndividually: false,
          individualPrice: null,
          isInCooldown: false,
          cooldownEndDate: null,
          daysUntilAvailable: null,
          userCredits: 0,
          canAffordWithCredits: false,
          creditsRequired: 0,
          primaryAction: 'not_available',
          primaryActionText: 'Not Available',
          primaryActionPrice: null,
          availabilityMessage: '',
          explanationMessage: '',
          statusBadge: { text: 'Unknown', color: 'gray' }
        };
      }
      
      // Check if user is admin
      let isAdmin = false;
      if (session?.user?.id) {
        const { data: adminData } = await supabase.rpc('is_admin_new', {
          user_id: session.user.id
        });
        isAdmin = adminData || false;
      }
      
      // Get book availability details
      const { data: bookData } = await supabase
        .from('books')
        .select('availability_type, cooldown_end_date, individual_price, is_preview')
        .eq('id', book.id)
        .single();
      
      // Check cooldown status
      const { data: isInCooldown } = await supabase.rpc('is_book_in_cooldown', {
        book_id: book.id
      });
      
      // Get user credits (only if no access)
      let userCredits = 0;
      if (session?.user?.id && !isAdmin && !hasIndividualAccess) {
        const { data: creditsData } = await supabase
          .from('user_credits')
          .select('balance')
          .eq('user_id', session.user.id)
          .maybeSingle();
        userCredits = creditsData?.balance || 0;
      }
      
      // Determine bundle status and access
      let bundleStatus: SmartAvailability['bundleStatus'] = null;
      let bundleTitle: string | null = null;
      let bundlePrice: number | null = null;
      let earlyAccessPrice: number | null = null;
      let hasBundleAccess = false;
      
      if (book.bundle_id === activeBundle?.id) {
        bundleStatus = 'active';
        bundleTitle = activeBundle.title;
        bundlePrice = activeBundle.price;
        earlyAccessPrice = activeBundle.early_access_price;
        hasBundleAccess = !!hasActiveAccess;
      } else {
        const permanentBundle = permanentBundles.find(b => b.id === book.bundle_id);
        if (permanentBundle) {
          bundleStatus = 'permanent';
          bundleTitle = permanentBundle.title;
          bundlePrice = permanentBundle.price;
          hasBundleAccess = !!permanentBundleAccess[book.bundle_id];
        } else {
          const expiredBundle = expiredBundles.find(b => b.id === book.bundle_id);
          if (expiredBundle) {
            bundleStatus = 'expired';
            bundleTitle = expiredBundle.title;
            bundlePrice = expiredBundle.price;
            hasBundleAccess = !!expiredBundleAccess[book.bundle_id];
          }
        }
      }
      
      // Check if subscription model is enabled and user has active subscription
      const isSubscriptionMode = appConfig?.current_model === 'subscription';
      const hasActiveSubscription = isSubscriptionMode && subscriptionStatus?.subscription_status === 'active';
      const hasCancelledWithTimeLeft = isSubscriptionMode && 
        subscriptionStatus?.subscription_status === 'cancelled' && 
        subscriptionStatus?.current_period_end && 
        new Date(subscriptionStatus.current_period_end) > new Date();
      const hasSubscriptionAccess = hasActiveSubscription || hasCancelledWithTimeLeft;
      
      // Check if book is a free preview
      const isPreviewBook = bookData?.is_preview === true;
      
      // Calculate availability
      const hasAccess = isAdmin || hasSubscriptionAccess || hasBundleAccess || hasIndividualAccess || isPreviewBook;
      const individualPrice = bookData?.individual_price || 3.99;
      
      // Individual purchase availability logic
      const canPurchaseIndividually = 
        // Always available if explicitly set to individual_only
        bookData?.availability_type === 'individual_only' ||
        // Available if it's bundle_or_individual (rare case)
        bookData?.availability_type === 'bundle_or_individual' ||
        // Available if it's from an expired temporal bundle (bypass cooldown)
        (bundleStatus === 'expired');
      
      // Calculate cooldown days
      let daysUntilAvailable: number | null = null;
      if (bookData?.cooldown_end_date && isInCooldown) {
        const cooldownEnd = new Date(bookData.cooldown_end_date);
        const now = new Date();
        const timeDiff = cooldownEnd.getTime() - now.getTime();
        daysUntilAvailable = Math.ceil(timeDiff / (1000 * 3600 * 24));
      }
      
      // Credits calculation (only when user doesn't have access)
      const creditsRequired = Math.ceil(individualPrice);
      const canAffordWithCredits = userCredits >= creditsRequired && canPurchaseIndividually && !hasAccess;
      
      // Determine primary action and messaging
      let primaryAction: SmartAvailability['primaryAction'];
      let primaryActionText: string;
      let primaryActionPrice: number | null = null;
      let availabilityMessage: string;
      let explanationMessage: string;
      let statusBadge: SmartAvailability['statusBadge'];
      
      // If user has access, show read action
      if (hasAccess) {
        primaryAction = 'read';
        primaryActionText = 'Read Now';
        
        if (hasCancelledWithTimeLeft) {
          const daysLeft = Math.ceil((new Date(subscriptionStatus!.current_period_end!).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
          availabilityMessage = `${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining on subscription`;
          explanationMessage = 'Reactivate to keep unlimited access';
          statusBadge = { text: `${daysLeft}d left`, color: 'orange' };
        } else if (hasActiveSubscription) {
          availabilityMessage = 'Included in your subscription';
          explanationMessage = 'Start reading immediately';
          statusBadge = { text: 'Available', color: 'green' };
        } else {
          availabilityMessage = 'You have access to this book';
          explanationMessage = 'Start reading immediately';
          statusBadge = { text: 'Available', color: 'green' };
        }
      }
      // If subscription model is enabled but user doesn't have subscription
      else if (isSubscriptionMode) {
        primaryAction = 'bundle'; // We'll use this for subscription signup
        primaryActionText = `Subscribe ($${appConfig?.weekly_price}/week or $${appConfig?.monthly_price}/month)`;
        primaryActionPrice = appConfig?.weekly_price;
        availabilityMessage = 'Subscribe for unlimited access to all books';
        explanationMessage = 'Get unlimited access to our entire library';
        statusBadge = { text: 'Requires Subscription', color: 'blue' };
      }
      // If book is in active bundle
      else if (bundleStatus === 'active') {
        primaryAction = 'bundle';
        primaryActionText = `Get Bundle ($${bundlePrice})`;
        primaryActionPrice = bundlePrice;
        availabilityMessage = `Part of this month's active bundle`;
        explanationMessage = `Get this book + others in ${bundleTitle}`;
        statusBadge = { text: 'In Active Bundle', color: 'blue' };
      }
      // If book is in upcoming bundle
      else if (bundleStatus === 'upcoming') {
        primaryAction = 'early_access';
        primaryActionText = `Pre-order Bundle ($${earlyAccessPrice})`;
        primaryActionPrice = earlyAccessPrice;
        availabilityMessage = 'Pre-order next month\'s bundle and get early access';
        explanationMessage = `Choose one book to unlock early from ${bundleTitle}`;
        statusBadge = { text: 'Early Access Available', color: 'purple' };
      }
      // If book is in permanent bundle
      else if (bundleStatus === 'permanent') {
        primaryAction = 'bundle';
        primaryActionText = `Get Collection ($${bundlePrice})`;
        primaryActionPrice = bundlePrice;
        availabilityMessage = `Part of the ${bundleTitle} collection`;
        explanationMessage = 'Get this book and all others in this permanent collection';
        statusBadge = { text: 'In Collection', color: 'blue' };
      }
      // If book is in cooldown (from expired bundle) - BYPASSED, skip to individual purchase
      // else if (bundleStatus === 'expired' && isInCooldown) {
      //   primaryAction = 'cooldown';
      //   primaryActionText = `Available in ${daysUntilAvailable} ${daysUntilAvailable === 1 ? 'day' : 'days'}`;
      //   availabilityMessage = 'This book is temporarily unavailable';
      //   explanationMessage = `Available for individual purchase in ${daysUntilAvailable} ${daysUntilAvailable === 1 ? 'day' : 'days'}`;
      //   statusBadge = { text: 'Cooldown', color: 'orange' };
      // }
      // If can purchase individually with credits
      else if (canPurchaseIndividually && canAffordWithCredits) {
        primaryAction = 'credits';
        primaryActionText = `Use ${creditsRequired} Credits`;
        availabilityMessage = 'Use your credits to unlock this book';
        explanationMessage = `You have ${userCredits} credits available`;
        statusBadge = { text: 'Use Credits', color: 'purple' };
      }
      // If can purchase individually with cash
      else if (canPurchaseIndividually) {
        primaryAction = 'individual';
        primaryActionText = `Buy Now ($${individualPrice})`;
        primaryActionPrice = individualPrice;
        availabilityMessage = 'Available for individual purchase';
        explanationMessage = bundleStatus === 'expired' 
          ? 'This book was in a previous bundle and is now available individually'
          : 'Available for individual purchase';
        statusBadge = { text: 'Buy Individual', color: 'green' };
      }
      // Book is not available
      else {
        primaryAction = 'not_available';
        primaryActionText = 'Not Available';
        availabilityMessage = 'This book is not currently available';
        explanationMessage = bundleStatus === 'expired'
          ? 'This book was in a previous bundle. Check back later for individual availability'
          : 'Check back later for availability updates';
        statusBadge = { text: 'Unavailable', color: 'gray' };
      }
      
      return {
        hasAccess,
        hasIndividualAccess: !!hasIndividualAccess,
        hasBundleAccess,
        isAdmin,
        bundleStatus,
        bundleTitle,
        bundlePrice,
        earlyAccessPrice,
        canPurchaseIndividually,
        individualPrice,
        isInCooldown: !!isInCooldown,
        cooldownEndDate: bookData?.cooldown_end_date || null,
        daysUntilAvailable,
        userCredits,
        canAffordWithCredits,
        creditsRequired,
        primaryAction,
        primaryActionText,
        primaryActionPrice,
        availabilityMessage,
        explanationMessage,
        statusBadge
      };
    },
    enabled: !!book,
    staleTime: 30000 // Cache for 30 seconds
  });
}
